import { EditOutlined } from '@ant-design/icons';
import { Button, Tooltip, Typography } from 'antd';
import { Link } from 'react-router-dom';

import type { ModelProduct } from '@app/api/Products';
import { EDIT_PRODUCT_PATH } from '@app/constants';
import type { GetColumnType } from '@app/pages/Warehouses/ProductDirectory/types';
import { bigNumberToString } from '@app/utils/bigNumberToString';

import type { ColumnsType } from 'antd/es/table';

const { Text } = Typography;

export const getProductDirectoryColumns: GetColumnType = ({ filtersLists }) => {
  const categories = filtersLists?.Categories ?? [];
  const brands = filtersLists?.Brands ?? [];

  const getUnitLabel = (unit?: string | null): string => {
    if (!unit) return '';

    const map: Record<string, string> = {
      pieces: 'шт',
      kilos: 'кг',
      milliliters: 'мл',
      grams: 'г',
      liters: 'л',
    };

    return map[unit] ?? unit;
  };

  const columns: ColumnsType<ModelProduct> = [
    {
      title: 'Название',
      dataIndex: 'Name',
      key: 'Name',
      ellipsis: true,
      width: 350,
      render: (text, row) => (
        <Tooltip title={text}>
          <Text className="no-wrap-text" ellipsis style={{ maxWidth: 300 }}>
            {text}
            {row.IsDeleted && (
              <>
                <br />
                <span className="deleted-label">В архиве</span>
              </>
            )}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: 'Категория',
      dataIndex: 'ProductCategoryId',
      key: 'ProductCategoryId',
      ellipsis: true,
      width: 160,
      render: (Id: number | null | undefined) => {
        const category =
          categories.find((item) => item.Id === Id)?.Name || 'Не указан';
        return <span className="no-wrap-text">{category}</span>;
      },
    },
    {
      title: 'Бренд',
      dataIndex: 'ProductBrandId',
      key: 'ProductBrandId',
      ellipsis: true,
      width: 150,
      render: (Id: number | null | undefined) => {
        const brand =
          brands.find((item) => item.Id === Id)?.Name || 'Не указан';
        return (
          <span className="no-wrap-text" style={{ maxWidth: 300 }}>
            {brand}
          </span>
        );
      },
    },
    {
      title: 'Единицы измерения',
      dataIndex: 'UnitOfMeasurement',
      key: 'UnitOfMeasurement',
      ellipsis: true,
      width: 130,
      render: (text: string) => (
        <span className="no-wrap-text" style={{ maxWidth: 300 }}>
          {getUnitLabel(text)}
        </span>
      ),
    },
    {
      title: 'Масса/объем',
      dataIndex: 'Amount',
      key: 'Amount',
      ellipsis: true,
      width: 110,
      render: (value: number | bigint) => {
        let display;

        if (typeof value === 'bigint') {
          display = value.toString();
        } else if (typeof value === 'number') {
          display = value;
        } else if (value && typeof value === 'object') {
          display = bigNumberToString(value);
        } else {
          display = '';
        }

        return <span className="no-wrap-text">{display}</span>;
      },
    },
    {
      title: 'Артикул',
      dataIndex: 'Article',
      key: 'Article',
      ellipsis: true,
      width: 130,
      render: (text: string) => (
        <span className="no-wrap-text" style={{ maxWidth: 300 }}>
          {text}
        </span>
      ),
    },
    {
      title: 'Штрихкод',
      dataIndex: 'BarCode',
      key: 'BarCode',
      ellipsis: true,
      width: 140,
      render: (text: string) => (
        <span className="no-wrap-text" style={{ maxWidth: 300 }}>
          {text}
        </span>
      ),
    },
    {
      title: 'Примечание',
      dataIndex: 'Comment',
      key: 'Comment',
      ellipsis: true,
      width: 140,
      render: (text: string) => (
        <span className="no-wrap-text" style={{ maxWidth: 300 }}>
          {text}
        </span>
      ),
    },
    {
      title: null,
      key: 'actions',
      width: 48,
      fixed: 'right',
      render: (_: unknown, row: ModelProduct) =>
        !row.IsDeleted && (
          <Link
            to={`${EDIT_PRODUCT_PATH}?id=${row.Id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button icon={<EditOutlined />} />
          </Link>
        ),
    },
  ];

  return columns;
};
